import { useCallback, useState } from 'react';
import { useContentEditLock } from './use-content-edit-lock';

/**
 * Mover uma questão de posição na prova, o único jeito de mudar a ordem desde
 * que a prova deixou de editar a questão (decisão 1 da história da tela da
 * questão).
 *
 * A posição é da prova, e não da questão: o lock é o do `examId`, e o back
 * confere a reordenação por ele. É **um** por página, e não um por card — o
 * `useContentEditLock` solta o lock ao desmontar, e o card de uma questão
 * excluída soltaria o lock que outro card está usando.
 *
 * `movingId` diz em qual card o controle está aberto. Trocar de card com o
 * lock já na mão não pede de novo.
 */
export function useExamQuestionMove(examId: string | undefined) {
  const lock = useContentEditLock(examId);
  const [movingId, setMovingId] = useState<string | null>(null);
  const { start, finish, editing } = lock;

  const begin = useCallback(
    (questionId: string) => {
      setMovingId(questionId);
      if (!editing) void start();
    },
    [editing, start],
  );

  const end = useCallback(() => {
    setMovingId(null);
    finish();
  }, [finish]);

  return { lock, movingId, begin, end };
}

export type ExamQuestionMove = ReturnType<typeof useExamQuestionMove>;
